/**
 * Korean locale for phone-numbers page.
 * Export is the "phoneNumbers" subtree for host i18n merge.
 */
import type { PhoneNumbersLocale } from './en'

export const phoneNumbersKo = {
  list: {
    title: `전화번호`,
    serverGroup: `서버 그룹:`,
    searchPlaceholder: `검색...`,
    loading: `전화번호 로딩 중...`,
    error: `전화번호를 불러오지 못했습니다`,
    empty: `이 PBX에 대한 전화번호가 없습니다`,
    noResults: `검색과 일치하는 전화번호가 없습니다`,
    columnName: `이름`,
    columnNumber: `번호`,
    columnExtension: `내선`,
    columnCallerId: `발신자 ID`,
    columnType: `유형`,
    columnTollFree: `수신자 부담`,
    columnSms: `SMS`,
    columnE911: `e911`,
    columnVoiceProvider: `음성 제공업체`,
    columnSmsProvider: `SMS 제공업체`,
    columnProviders: `제공업체`,
    columnStatus: `상태`,
    columnUpdated: `수정일`,
    yes: `예`,
    no: `아니오`,
    smsActive: `활성`,
    e911Verified: `인증됨`,
    createButton: `+ 전화번호 생성`,
    createDisabledTooltip: `전화번호 생성은 전화 팀에서 관리합니다`,
    filterButton: `필터`,
    copyNumber: `번호 복사`,
    toast: {
      createSuccess: `전화번호가 생성되었습니다`,
      createFailed: `전화번호 생성에 실패했습니다`,
      copied: `번호가 클립보드에 복사되었습니다`,
      copyFailed: `번호 복사에 실패했습니다`,
    },
  },
  filters: {
    status: `상태`,
    allStatuses: `전체`,
    type: `유형`,
    allTypes: `전체`,
    voiceProvider: `음성 제공업체`,
    smsProvider: `SMS 제공업체`,
    allProviders: `전체`,
    tollFree: `무료 전화`,
    any: `모두`,
    showDeleted: `삭제된 항목 표시`,
    clearAll: `필터 초기화`,
  },
  types: {
    callerId: `발신자 ID`,
    smsOnly: `SMS 전용`,
    standard: `표준`,
    temporary: `임시`,
  },
  bulk: {
    title: `상태 일괄 변경`,
    description: `선택한 전화번호의 상태를 변경합니다.`,
    selected: `{{count}}개 선택됨`,
    apply: `적용`,
    updateStatus: `상태 변경`,
    clearSelection: `선택 해제`,
    maxExceeded: `한 번에 최대 25개`,
    selectAll: `모든 전화번호 선택`,
    selectRow: `{{number}} 선택`,
    toast: {
      success: `상태가 성공적으로 업데이트되었습니다`,
      failed: `일괄 상태 업데이트에 실패했습니다`,
    },
    results: {
      titleSuccess: `일괄 업데이트 완료`,
      titleAllFailed: `일괄 업데이트 실패`,
      titlePartial: `일괄 업데이트 부분 완료`,
      summarySuccess: `{{count}}개의 전화번호가 성공적으로 업데이트되었습니다.`,
      summaryAllFailed: `{{count}}개의 전화번호 모두 업데이트에 실패했습니다.`,
      summaryPartial: `{{totalCount}}개 중 {{successCount}}개 업데이트됨. {{failedCount}}개 실패:`,
      colNumber: `번호`,
      colStatus: `현재 상태`,
      colError: `오류`,
      close: `닫기`,
    },
  },
  detail: {
    loading: `전화번호 로딩 중...`,
    error: `전화번호를 불러오지 못했습니다`,
    backToList: `목록으로 돌아가기`,
    editButton: `전화번호 편집`,
    deleteTitle: `전화번호 삭제`,
    deleteButton: `삭제`,
    deleteConfirmation: `전화번호가 일시 삭제됩니다. 필요한 경우 나중에 복원할 수 있습니다. 계속하시겠습니까?`,
    restoreButton: `복원`,
    noopSaveButton: `다시 저장`,
    deleted: `삭제됨`,
    sectionGeneral: `일반 정보`,
    sectionProviders: `제공업체`,
    sectionRouting: `라우팅`,
    sectionSms: `SMS`,
    sectionE911: `E911 정보`,
    sectionTimestamps: `타임스탬프`,
    sectionSmsDiagnostics: `Telnyx SMS 진단`,
    diagnosticWarning: `경고`,
    fieldName: `이름`,
    fieldNumber: `번호`,
    fieldPbx: `PBX`,
    fieldType: `유형`,
    fieldCallerId: `발신자 ID`,
    fieldExtension: `내선`,
    fieldTollFree: `수신자 부담`,
    fieldCountryCode: `국가 코드`,
    fieldLocalPresence: `로컬 프레즌스`,
    fieldCampaignId: `캠페인 ID`,
    fieldVoiceProvider: `음성 제공업체`,
    fieldSmsProvider: `SMS 제공업체`,
    fieldSmsEnabled: `SMS 활성화`,
    fieldSmsStatus: `SMS 상태`,
    fieldSmsRelay: `SMS 릴레이`,
    fieldSmsRelayIntegration: `SMS 릴레이 통합`,
    fieldSmsGroup: `SMS 그룹`,
    fieldE911Status: `E911 상태`,
    fieldE911Address: `주소`,
    fieldE911Address2: `주소 2`,
    fieldE911City: `도시`,
    fieldE911State: `주`,
    fieldSendFaxesFrom: `팩스 발신 번호`,
    usePbxDefault: `PBX 기본값 사용`,
    fieldPbxServerGroup: `PBX 서버 그룹`,
    fieldSourceTracking: `소스 추적`,
    fieldStatus: `상태`,
    fieldE911PostalCode: `우편번호`,
    fieldCreatedAt: `생성일`,
    fieldUpdatedAt: `수정일`,
    viewInProvider: `{{provider}}에서 보기`,
    sectionMetrics: `메트릭`,
    fieldCallStats: `통화 통계`,
    callStatsLink: `통화 통계`,
    on: `켜기`,
    off: `끄기`,
    registered: `등록됨`,
    inactive: `비활성`,
    notRegistered: `미등록`,
    toast: {
      updateSuccess: `전화번호가 업데이트되었습니다`,
      updateFailed: `전화번호 업데이트에 실패했습니다`,
      deleteSuccess: `전화번호가 삭제되었습니다`,
      deleteFailed: `전화번호 삭제에 실패했습니다`,
      restoreSuccess: `전화번호가 복원되었습니다`,
      restoreFailed: `전화번호 복원에 실패했습니다`,
      noopSaveSuccess: `사이드 이펙트가 다시 트리거되었습니다`,
      noopSaveFailed: `다시 저장에 실패했습니다`,
    },
  },
  edit: {
    title: `전화번호 편집`,
    numberLabel: `번호`,
    nameLabel: `이름`,
    namePlaceholder: `전화번호 이름`,
    typeLabel: `유형`,
    statusLabel: `상태`,
    selectStatus: `— 상태 선택 —`,
    voiceProviderLabel: `음성 제공업체`,
    smsProviderLabel: `SMS 제공업체`,
    selectProvider: `— 선택 —`,
    unassigned: `미할당`,
    callerIdLabel: `발신자 ID`,
    sendFaxesFromLabel: `팩스 발신 번호`,
    usePbxDefault: `PBX 기본값 사용`,
    extensionLabel: `내선`,
    extensionPlaceholder: `내선 UUID`,
    extensionHelp: `전화번호의 선택적 내선입니다.`,
    noExtension: `없음`,
    sourceTrackingLabel: `소스 추적`,
    sourceTrackingHelp: `수신 통화의 발신자 ID 앞에 최대 6자를 추가합니다. 이는 일반적으로 광고 캠페인을 추적하는 데 사용됩니다.`,
    localPresenceLabel: `로컬 프레즌스`,
    localPresenceHelp: `로컬 프레즌스 그룹은 로컬 프레즌스가 활성화된 회선 내선이 수신자에게 지역 번호로 표시되도록 발신할 수 있는 번호 풀을 생성합니다.`,
    e911Section: `긴급 911 (E911) 설정`,
    e911AddressLabel: `주소`,
    e911Address2Label: `아파트/호실`,
    e911CityLabel: `도시`,
    e911StateLabel: `주/도`,
    e911PostalCodeLabel: `우편번호`,
    e911Disclaimer: `E911은 북미에서 발신자의 위치를 911 교환원에게 자동으로 제공하는 시스템입니다. 이 정보가 정확한지 확인하세요. 누군가의 생명이 이에 달려있을 수 있습니다.`,
    smsSection: `SMS 설정`,
    smsEnabledLabel: `SMS 활성화`,
    smsEnabledWarning: `경고: SMS를 활성화하면 이 번호의 eSPID 이전 요청이 시작되어 고객에게 SMS 다운타임이 발생할 수 있습니다. 진행하기 전에 원하는 작업인지 확인하세요.`,
    smsRelayLabel: `SMS 릴레이`,
    smsRelayIntegrationLabel: `SMS 릴레이 통합`,
    smsRelayIntegrationDisabledHelp: `통합을 선택하려면 SMS 릴레이를 사용 설정하세요.`,
    noIntegration: `없음`,
    integrationSearchPlaceholder: `통합 검색...`,
    integrationSearchAriaLabel: `이름으로 SMS 릴레이 통합 검색`,
    noIntegrationMatches: `검색과 일치하는 통합이 없습니다.`,
    campaignIdLabel: `캠페인 ID`,
    campaignIdHelp: `SMS 전송에 필요합니다. 이 값을 설정하기 전에 해당 제공업체에서 캠페인 ID가 이 번호에 연결되어 있는지 확인하세요. 그렇지 않으면 서비스 중단, 전달 실패 및/또는 벌금이 발생할 수 있습니다.`,
    titleWithNumber: `전화번호 수정 — {{number}}`,
    advancedSection: `고급`,
    e911NotConfigured: `구성되지 않음`,
    extensionSearchPlaceholder: `내선 검색...`,
    extensionSearchAriaLabel: `이름 또는 번호로 내선 검색`,
    noExtensionMatches: `검색과 일치하는 내선이 없습니다.`,
    cancel: `취소`,
    save: `저장`,
  },
  create: {
    title: `전화번호 생성`,
    numberLabel: `전화번호`,
    numberPlaceholder: `+1234567890`,
    numberHelp: `국가 코드를 포함해야 합니다 (예: 미국 +18015551234, 영국 +442079460958).`,
    numberHelpWithPrefix: `국가 코드 없이 번호를 입력하세요.`,
    selectType: `— 유형 선택 —`,
    saveButton: `생성`,
    createMore: `더 생성`,
    searchStateProvince: `검색...`,
    noStateProvinceMatches: `일치 항목 없음`,
  },
  validation: {
    numberRequired: `전화번호는 필수입니다`,
    numberInvalid: `유효한 전화번호를 입력하세요 (예: +1 (801) 555-1234)`,
    numberInvalidNational: `유효한 전화번호를 입력하세요 (예: (801) 555-1234)`,
    numberMissingCountryCode: `전화번호에 국가 코드를 포함해야 합니다 (예: 미국 +1, 영국 +44)`,
    nameRequired: `이름은 필수입니다`,
    typeRequired: `유형은 필수입니다`,
    statusRequired: `상태는 필수입니다`,
    voiceProviderRequired: `음성 공급자는 필수입니다`,
    callerIdTooLong: `최대 15자`,
    callerIdInvalidChars: `문자, 숫자 및 공백만 허용됩니다`,
    callerIdAllDigits: `숫자만으로 구성할 수 없습니다`,
    campaignIdFormat: `정확히 7자의 영숫자여야 합니다`,
    sourceTrackingFormat: `1-6자의 영숫자만 가능`,
    e911Incomplete: `E911 필드가 하나라도 입력되면 주소, 도시, 주, 우편번호가 모두 필요합니다`,
  },
  pagination: {
    resultsPerPage: `페이지당 결과:`,
    previousPage: `이전 페이지`,
    nextPage: `다음 페이지`,
    jumpToPage: `{{page}}페이지로 이동`,
    all: `전체`,
    recordRange: `{{start}}-{{end}} / 총 {{total}}개`,
    recordRangeEmpty: `0 / 0`,
  },
} satisfies PhoneNumbersLocale
